// Age-verification screen: shows the camera while a selfie is taken for the
// self-hosted age check, the waiting state while a human reviews a borderline case,
// and a RETRY state when the photo was not good enough (so the user is never stuck on
// "Checking..."). ASCII only. See packages/server age pipeline.

import { t } from '../i18n.js';
import { el, ORB_SVG } from '../dom.js';
import { MIN_AGE } from '@lumi/shared';

export function renderVerify({ onCancel }) {
  const video = el('video', { autoplay: '', playsinline: '', muted: '', class: 'verify-video' });
  const title = el('h2', { text: t('Verifying your age') });
  const sub = el('p', { class: 'verify-sub', text: t('Getting your camera ready...') });
  const spinner = el('div', { class: 'verify-spinner hidden' }, [
    el('span', {}),
    el('span', {}),
    el('span', {}),
  ]);

  // Retry / error actions (hidden until showError is called).
  const retryBtn = el('button', { class: 'btn btn-primary', type: 'button', text: t('Try again') });
  const backBtn = el('button', { class: 'btn btn-ghost', type: 'button', text: t('Back') });
  const actions = el('div', { class: 'verify-actions hidden' }, [retryBtn, backBtn]);

  // The birth year, on the retry screen, only when the refusal was about the
  // year. That message tells the person to check the year they entered, and
  // the only place to change it was the landing, behind a Back button that
  // ends the whole attempt. The label is the landing's own.
  const yearSelect = el('select', { id: 'verifyBirthYear', 'aria-label': 'Birth year' });
  {
    // No year that would make somebody under 13. The landing refuses those
    // before the camera opens; offered here, one would reopen the camera and
    // upload a burst of a self-declared child before the server said no.
    const currentYear = new Date().getFullYear();
    for (let y = currentYear - MIN_AGE; y >= currentYear - 100; y -= 1) {
      yearSelect.append(el('option', { value: String(y), text: String(y) }));
    }
  }
  const yearBox = el('div', { class: 'verify-year hidden' }, [
    el('label', { for: 'verifyBirthYear', text: t('Birth year') }),
    yearSelect,
  ]);
  let yearOffered = false;
  const cancelBtn = el('button', { class: 'btn btn-ghost', type: 'button', text: t('Cancel'), onClick: onCancel });
  const frame = el('div', { class: 'verify-frame' }, [video, el('div', { class: 'verify-ring' })]);

  // Review thread: hidden until a moderator sends the first question. Replies go
  // through the sender installed by enableChat (see age.js onChatReady).
  let chatSender = null;
  let chatRenderedSig = '';
  const chatMsgs = el('div', { class: 'verify-chat-msgs' });
  const chatInput = el('input', {
    type: 'text',
    maxlength: '500',
    placeholder: t('Type your reply...'),
    'aria-label': 'Reply to the review team',
  });
  const chatSend = el('button', { class: 'btn btn-primary btn-sm', type: 'button', text: t('Send') });
  const chatBox = el('div', { class: 'verify-chat hidden' }, [
    el('div', { class: 'verify-chat-title', text: t('Message from the review team') }),
    chatMsgs,
    el('div', { class: 'verify-chat-row' }, [chatInput, chatSend]),
  ]);
  function renderChat(list) {
    const sig = JSON.stringify(list);
    if (sig === chatRenderedSig) return;
    chatRenderedSig = sig;
    chatMsgs.replaceChildren(
      ...list.map((m) =>
        el('div', { class: `verify-chat-msg ${m.from === 'admin' ? 'from-admin' : 'from-you'}` }, [
          el('strong', { text: m.from === 'admin' ? 'Review team: ' : 'You: ' }),
          el('span', { text: m.text }),
        ]),
      ),
    );
    chatBox.classList.remove('hidden');
    chatMsgs.scrollTop = chatMsgs.scrollHeight;
  }
  async function submitReply() {
    const text = chatInput.value.trim();
    if (!text || !chatSender || chatSend.disabled) return;
    chatSend.disabled = true;
    const updated = await chatSender(text);
    chatSend.disabled = false;
    if (updated) {
      chatInput.value = '';
      renderChat(updated);
    }
  }
  chatSend.addEventListener('click', submitReply);
  chatInput.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') submitReply();
  });

  // Live check: the review team can ask for a brief live camera view (a still
  // can be too blurry to judge). Nothing starts until the user taps Start --
  // the camera is never opened silently.
  let liveHandledOffer = '';
  let livePc = null;
  let liveMedia = null;
  const liveSelf = el('video', { autoplay: '', playsinline: '', muted: '', class: 'verify-live-self hidden' });
  const remoteAudio = el('audio', { autoplay: '' });
  const liveStatus = el('div', { class: 'verify-sub', text: '' });
  const liveStartBtn = el('button', { class: 'btn btn-primary btn-sm', type: 'button', text: t('Start live check') });
  const liveNoSoundBtn = el('button', { class: 'btn btn-ghost btn-sm hidden', type: 'button', text: t('Continue without sound') });
  const liveBox = el('div', { class: 'verify-live hidden' }, [
    el('div', { class: 'verify-chat-title', text: t('Live check requested') }),
    el('p', {
      class: 'verify-sub',
      text: t('A reviewer would like a brief live camera check to confirm your age. Tap Start when you are ready: they can see and talk to you until the check is decided.'),
    }),
    liveSelf,
    liveStatus,
    liveStartBtn,
    liveNoSoundBtn,
    remoteAudio,
  ]);
  function stopLive() {
    try { livePc?.close(); } catch { /* already closed */ }
    livePc = null;
    liveMedia?.getTracks().forEach((track) => track.stop());
    liveMedia = null;
  }
  const waitIce = (pc, ms) =>
    new Promise((resolve) => {
      if (pc.iceGatheringState === 'complete') { resolve(); return; }
      const iceTimer = setTimeout(resolve, ms);
      pc.addEventListener('icegatheringstatechange', () => {
        if (pc.iceGatheringState === 'complete') { clearTimeout(iceTimer); resolve(); }
      });
    });
  // Front camera as a hard requirement (a soft preference can lose to other
  // constraints and open the REAR camera); fall back for devices without one.
  const getFrontMedia = async (withAudio) => {
    try {
      return await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { exact: 'user' } },
        audio: withAudio,
      });
    } catch (err) {
      if (err && (err.name === 'OverconstrainedError' || err.name === 'NotFoundError')) {
        return navigator.mediaDevices.getUserMedia({ video: { facingMode: 'user' }, audio: withAudio });
      }
      throw err;
    }
  };
  async function startLive(req, skipMic = false) {
    liveStartBtn.disabled = true;
    liveNoSoundBtn.classList.add('hidden');
    liveStatus.textContent = 'Starting your camera...';
    // The reviewer needs to HEAR the user (a voice answer is part of the
    // check), so a blocked mic pauses for a decision instead of silently
    // continuing without sound. The offer is not answered yet, so fixing the
    // permission and trying again works cleanly. "Continue without sound"
    // still TRIES the mic (the browser may prompt right then and the user may
    // allow it -- the sound must then actually be used); it only proceeds
    // camera-only when the mic is truly refused.
    if (!skipMic) {
      try {
        liveMedia = await getFrontMedia(true);
      } catch {
        stopLive();
        liveStartBtn.disabled = false;
        liveStartBtn.textContent = 'Try again with sound';
        liveNoSoundBtn.classList.remove('hidden');
        liveStatus.textContent =
          'Your microphone is blocked, and the reviewer needs to hear you. Allow the microphone for this site (lock icon by the address bar, or your phone settings), then tap Try again with sound.';
        return;
      }
    } else {
      try {
        liveMedia = await getFrontMedia(true);
      } catch {
        try {
          liveMedia = await getFrontMedia(false);
        } catch (err) {
          stopLive();
          liveStartBtn.disabled = false;
          liveStatus.textContent =
            err && err.name === 'NotAllowedError'
              ? 'Camera access was blocked. Allow the camera for this site in your browser settings, then try again.'
              : `Could not start your camera (${(err && err.name) || 'unknown error'}). Close other apps using it and try again.`;
          return;
        }
      }
    }
    // Trust the stream, not the button: sound is on iff an audio track exists.
    const hasMic = (liveMedia.getAudioTracks?.() || []).length > 0;
    try {
      const cfg = req.ice
        ? { iceServers: req.ice.iceServers, iceTransportPolicy: req.ice.iceTransportPolicy }
        : {};
      livePc = new RTCPeerConnection(cfg);
      for (const track of liveMedia.getTracks()) livePc.addTrack(track, liveMedia);
      livePc.ontrack = (e) => { if (e.streams[0]) remoteAudio.srcObject = e.streams[0]; };
      livePc.onconnectionstatechange = () => {
        if (['failed', 'disconnected', 'closed'].includes(livePc?.connectionState)) {
          liveStatus.textContent = 'The live check has ended.';
          liveSelf.classList.add('hidden');
          stopLive();
        }
      };
      await livePc.setRemoteDescription(JSON.parse(req.offer));
      const answer = await livePc.createAnswer();
      await livePc.setLocalDescription(answer);
      await waitIce(livePc, 4000);
      const ok = await req.sendAnswer(JSON.stringify(livePc.localDescription));
      if (!ok) throw Object.assign(new Error('send failed'), { name: 'AnswerRejected' });
      liveSelf.srcObject = liveMedia;
      liveSelf.classList.remove('hidden');
      liveStartBtn.classList.add('hidden');
      liveNoSoundBtn.classList.add('hidden');
      liveStatus.textContent = hasMic
        ? 'Live check in progress: the reviewer can see and hear you.'
        : 'Live check in progress: the reviewer can see you (your mic is off; they can still talk to you).';
    } catch (err) {
       
      console.warn('live check failed', err);
      stopLive();
      liveStartBtn.disabled = false;
      liveStatus.textContent = `Could not start the live check (${(err && err.name) || 'error'}). Try again.`;
    }
  }
  let pendingLiveReq = null;
  liveStartBtn.addEventListener('click', () => {
    if (pendingLiveReq) startLive(pendingLiveReq);
  });
  liveNoSoundBtn.addEventListener('click', () => {
    if (pendingLiveReq) startLive(pendingLiveReq, true);
  });
  // Clear both review panels (live check + thread). Used when the check ends in
  // an error/rejection, so a stale "Start live check" or old thread never sits
  // next to the error message -- and a retry starts a NEW check with fresh state.
  function resetReviewPanels() {
    stopLive();
    pendingLiveReq = null;
    liveHandledOffer = '';
    liveBox.classList.add('hidden');
    liveSelf.classList.add('hidden');
    liveStartBtn.classList.remove('hidden');
    liveStartBtn.disabled = false;
    liveStartBtn.textContent = 'Start live check';
    liveNoSoundBtn.classList.add('hidden');
    liveStatus.textContent = '';
    chatBox.classList.add('hidden');
    chatRenderedSig = '';
    chatMsgs.replaceChildren();
    chatSender = null;
  }

  const root = el('div', { class: 'verify' }, [
    el('div', { class: 'verify-inner' }, [
      el('div', { class: 'topbar' }, [el('span', { html: ORB_SVG }), 'Lumi']),
      el('div', { class: 'verify-card' }, [frame, title, sub, spinner, liveBox, chatBox, yearBox, actions, cancelBtn]),
      el('p', {
        class: 'legal',
        text:
          'We confirm your age with our own check, running on our own servers: no outside company is involved. Your photo is used only for this check; a person reviews the decision, and the photo is deleted the moment they have (90 days at the latest).',
      }),
    ]),
  ]);

  return {
    root,
    api: {
      setVideo(stream) {
        video.srcObject = stream;
      },
      setStatus(text, s) {
        title.textContent = text;
        if (s != null) sub.textContent = s;
      },
      showSpinner(on) {
        spinner.classList.toggle('hidden', !on);
      },
      /** Turn the screen into a light for the photos (a dim camera picture), or back. */
      setLight(on) {
        root.classList.toggle('verify-lit', !!on);
      },
      /** Install the reply sender (called once the check enters human review). */
      enableChat(send) {
        chatSender = send;
      },
      /** Render the review thread (called from the status poll on updates). */
      setChatMessages(list) {
        if (Array.isArray(list) && list.length) renderChat(list);
      },
      /** A live-check request from the review team (called from the status poll). */
      offerLiveCheck(req) {
        if (!req || !req.offer || req.offer === liveHandledOffer) return;
        liveHandledOffer = req.offer;
        stopLive(); // a re-request replaces any previous session
        pendingLiveReq = req;
        liveStartBtn.disabled = false;
        liveStartBtn.textContent = 'Start live check';
        liveStartBtn.classList.remove('hidden');
        liveNoSoundBtn.classList.add('hidden');
        liveSelf.classList.add('hidden');
        liveStatus.textContent = '';
        liveBox.classList.remove('hidden');
      },
      /** Stop camera/mic and close the live connection (verification finished). */
      stopLive,
      /**
       * Show a recoverable error with a retry (and/or back) choice, keeping the user on
       * this screen -- never a frozen "Checking...". Resolves to 'retry' or 'back'.
       * Pass `birthYear` to put the year on screen beside the retry, preselected;
       * read what the person left it at with birthYear() afterwards.
       */
      showError(message, { retry = true, birthYear = null } = {}) {
        resetReviewPanels();
        spinner.classList.add('hidden');
        frame.classList.add('hidden');
        cancelBtn.classList.add('hidden');
        title.textContent = retry ? 'Let us try that again' : 'We could not verify you';
        sub.textContent = message || 'Something went wrong. Please try again.';
        retryBtn.classList.toggle('hidden', !retry);
        yearOffered = retry && Number.isInteger(birthYear)
          && [...yearSelect.options].some((o) => o.value === String(birthYear));
        if (yearOffered) yearSelect.value = String(birthYear);
        yearBox.classList.toggle('hidden', !yearOffered);
        actions.classList.remove('hidden');
        return new Promise((resolve) => {
          const done = (choice) => {
            yearBox.classList.add('hidden');
            actions.classList.add('hidden');
            frame.classList.remove('hidden');
            cancelBtn.classList.remove('hidden');
            retryBtn.onclick = null;
            backBtn.onclick = null;
            resolve(choice);
          };
          retryBtn.onclick = () => done('retry');
          backBtn.onclick = () => done('back');
        });
      },
      /** The year chosen on the last retry screen that offered one, else null. */
      birthYear() {
        return yearOffered ? Number(yearSelect.value) : null;
      },
    },
  };
}
